import React, { useEffect, useMemo, useState } from 'react';

import { isEqual, omit } from 'lodash';
import { Grid, Typography, Box, Divider } from '@material-ui/core';

import { CardComponent, PageHeader, SettingsForm, TabPanelItem, TabsComponent } from 'components';
import { AutocompleteValidatorComponent, TextValidatorComponent } from 'components/FormComponents';
import { VALIDATORS_MAP } from 'components/FormComponents/const';
import { NAVIGATION_TYPE } from 'components/LinkBehavior/constants';
import { useBusyProgressActions } from 'contexts/BusyProgressContext';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { ROUTES } from 'routes/constants';
import { errorResponseModel } from 'models';
import { analyticsProviderModel } from 'models/AnalyticsProviderModel';
import { useSubmittingState } from 'hooks';
import { Planogram } from 'types';
import { HostErrorResponsePlanogramData, MatomoAnalyticsForm } from 'types/other';
import { getTWithPathKey } from 'utils';

import { Subtext } from '../GoogleTagManager/style';
import { Switch } from 'components/style';

const defaultSettings: Partial<MatomoAnalyticsForm> = {
  navigationTypeB: null,
  navigationValueB: null,
  planogramIdA: null,
  testName: null,
  variantName: null,
};

const tabKeys = Object.values(omit(NAVIGATION_TYPE, 'back'));

const AbTesting = () => {
  const [tabValue, setTabValue] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [settings, setSettings] = useState<Partial<MatomoAnalyticsForm>>(defaultSettings);
  const [initialSettings, setInitialSettings] = useState<Partial<MatomoAnalyticsForm>>(defaultSettings);
  const [planogramsList, setPlanogramsList] = useState<HostErrorResponsePlanogramData[]>([]);
  const { withPageProgressHandler } = useBusyProgressActions();
  const getT = getTWithPathKey('pages.settings.analyticsProviders.abTesting');
  const { handleServerError } = useServerErrorActions();
  const { isSubmitting, withHandlingSubmittingState } = useSubmittingState();

  useEffect(() => {
    getSettings();
    getPlanogramsList();
  }, []);

  const getSettings = withPageProgressHandler(() =>
    analyticsProviderModel
      .getMatomoSettings()
      .then(analyticsProvider => {
        const savedSettings = {
          ...analyticsProvider,
          navigationTypeB: analyticsProvider.navigationTypeB ?? NAVIGATION_TYPE.internal,
        };

        saveSettings(savedSettings);
        setTabValue(tabKeys.indexOf(savedSettings.navigationTypeB));
        setEnabled(!!savedSettings.planogramIdA && !!savedSettings.navigationValueB);
      })
      .catch(error => handleServerError(error)),
  );

  const getPlanogramsList = withPageProgressHandler(() =>
    errorResponseModel
      .getHostErrorResponses()
      .then(({ planograms }) => setPlanogramsList(planograms))
      .catch(error => handleServerError(error)),
  );

  const saveSettings = (data: Partial<MatomoAnalyticsForm>) => {
    setSettings(data);
    setInitialSettings(data);
  };

  const renderProviderSwitch = () => (
    <Switch
      onChange={() =>
        setEnabled(prevState => {
          if (prevState) {
            setSettings(prevState => ({
              ...prevState,
              ...defaultSettings,
            }));
          }

          return !prevState;
        })
      }
      checked={enabled}
    />
  );

  const handleSubmit = withHandlingSubmittingState(() =>
    analyticsProviderModel
      .updateMatomoSettings({ analyticsProvider: settings })
      .then(analyticsProvider => {
        saveSettings(analyticsProvider);
      })
      .catch(error => handleServerError(error)),
  );

  const handleChange = (item: Record<string, string | number>) => {
    setSettings(prevState => ({
      ...prevState,
      ...item,
    }));
  };

  const updateAutocompleteValue = (val: number) => {
    setTabValue(val);
    setSettings(prevState => ({
      ...prevState,
      navigationValueB: '',
      navigationTypeB: tabKeys[val],
    }));
  };

  const showSaveButton = useMemo(() => !isEqual(initialSettings, settings), [settings, initialSettings]);

  return (
    <>
      <PageHeader title={getT('pageTitle')} backLink={ROUTES.analyticsProviders} />
      <SettingsForm isSaveButtonVisible={showSaveButton} handleSave={handleSubmit} id="gtm">
        <CardComponent title={getT('cardTitle')} isLoading={isSubmitting} CardHeaderNav={renderProviderSwitch}>
          {enabled && (
            <Grid container spacing={6} direction="column">
              <Grid item>
                <Subtext>{getT('cardSubtext')}</Subtext>
              </Grid>
              <Grid item>
                <Divider />
              </Grid>
              <Grid item>
                <Box mb={4}>
                  <Typography variant="h4">{getT('testNameLabel')}</Typography>
                </Box>
                <TextValidatorComponent
                  label={getT('testNameLabel')}
                  value={settings.testName ?? ''}
                  fieldName="testName"
                  handleChange={handleChange}
                  validators={[
                    VALIDATORS_MAP.required,
                    VALIDATORS_MAP.onlyLettersNumbers,
                    VALIDATORS_MAP.maxStringLength(50),
                  ]}
                  disableMinHeight
                />
                <Subtext>{getT('testNameValidationText')}</Subtext>
              </Grid>
              <Grid item>
                <Box mb={4}>
                  <Typography variant="h4">{getT('variationTitle1')}</Typography>
                </Box>
                <AutocompleteValidatorComponent
                  value={Number(settings.planogramIdA)}
                  fieldName="planogramIdA"
                  handleChange={handleChange}
                  selectList={planogramsList}
                  itemsNamePath="urlPath"
                  label="Select sphere"
                  validators={[VALIDATORS_MAP.required]}
                  disableMinHeight
                />
              </Grid>
              <Grid item>
                <Box mb={4}>
                  <Typography variant="h4">{getT('variationTitle2')}</Typography>
                </Box>
                <Box mb={6}>
                  <TextValidatorComponent
                    label={getT('variationNameLabel')}
                    value={settings.variantName ?? ''}
                    fieldName="variantName"
                    handleChange={handleChange}
                    validators={[
                      VALIDATORS_MAP.required,
                      VALIDATORS_MAP.onlyLettersNumbers,
                      VALIDATORS_MAP.maxStringLength(50),
                    ]}
                    disableMinHeight
                  />
                  <Subtext>{getT('testNameValidationText')}</Subtext>
                </Box>
                <Box mb={3}>
                  <TabsComponent
                    value={tabValue}
                    setValue={val => updateAutocompleteValue(val)}
                    tabNames={['Sphere', 'Website']}
                  />
                </Box>
                <TabPanelItem value={tabValue} index={0}>
                  <AutocompleteValidatorComponent
                    value={Number(settings.navigationValueB)}
                    fieldName="navigationValueB"
                    handleChange={handleChange}
                    selectList={planogramsList}
                    itemsNamePath="urlPath"
                    label="Select sphere"
                    validators={tabValue === 0 ? [VALIDATORS_MAP.required] : []}
                    disableMinHeight
                  />
                </TabPanelItem>
                <TabPanelItem value={tabValue} index={1}>
                  <TextValidatorComponent
                    label={getT('websiteUrlLabel')}
                    value={settings.navigationValueB ?? ''}
                    fieldName="navigationValueB"
                    handleChange={handleChange}
                    validators={tabValue === 1 ? [VALIDATORS_MAP.required] : []}
                    disableMinHeight
                  />
                </TabPanelItem>
              </Grid>
              <Grid item>
                <Divider />
              </Grid>
              <Grid item>
                <Subtext style={{ paddingTop: '0' }}>{getT('disclaimer')}</Subtext>
              </Grid>
            </Grid>
          )}
        </CardComponent>
      </SettingsForm>
    </>
  );
};

export default AbTesting;
